import { ACTIVITY_SOURCES } from '@/utils/constants'
import { flatten, isEmpty, isNil, orderBy, take } from 'lodash'

import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import utc from 'dayjs/plugin/utc'
dayjs.extend(isBetween)
dayjs.extend(utc)

/**
 *
 * @param {Array} data
 * @returns {Array}
 */
export const mapActivity = data =>
    Object.keys(data)
        .filter(key => key !== 'id')
        .map(key => ({ source: ACTIVITY_SOURCES[key], data: data[key] }))


/**
 *
 * @param {Date} start
 * @returns {String}
 */
export const toStartISO = start =>
    dayjs(start)
        .startOf('day')
        .toISOString()

/**
 *
 * @param {Date} end
 * @returns {String}
 */
export const toEndISO = end => {
    const endISO = dayjs(end)
        .add(1, 'day')
        .startOf('day')
        .toISOString()

    return dayjs(endISO).isBefore() ? endISO : dayjs().toISOString()
}
export const toStartISOFormatted = start =>
    dayjs(start)
        .startOf('day')
        .format('YYYY-MM-DDTHH:mm:ss[Z]')
/**
 *
 * @param {Date} end
 * @returns {String}
 */
export const toEndISOFormatted = end => {
    const endISO = dayjs(end)
        .add(1, 'day')
        .startOf('day')
    const formattedEndISO = dayjs(endISO).isBefore() ? endISO : dayjs()
    return formattedEndISO.format('YYYY-MM-DDTHH:mm:ss[Z]')
}

export const isLessThan3Days = (d1, d2) => {
    const diffInHours = Math.abs(dayjs(d1).diff(dayjs(d2), 'hour'))
    return diffInHours <= 72
}

export const mapTaxonomiesBreakdown = (array, field) => {
    return array?.map(cluster => {
        return {
            id: cluster.id,
            name: cluster.label,
            value: field === 'affinity' ? (cluster[field]).toFixed(2) : cluster[field].toFixed(1)
        }
    })
}

export const getAccountAllocationMetrics = (allocations, timeUnit = 'day', kibanaData, start, end) => {
    const formatDay = 'YYYY-MM-DD'
    const formatHour = 'YYYY-MM-DD HH:mm'
    const format = timeUnit === 'day' ? formatDay : formatHour
    const startDay = dayjs(start).format(formatDay)
    const endDay = dayjs(end).format(formatDay)
    const verifyDateBetweenStandAndEndDays = pdate => dayjs(pdate).isBetween(startDay, endDay, 'day', '[]')
    // [TODO] remove filter method to remove later when backend handle with timezone
    if (allocations !== null) {
        const allAllocations = flatten(
            allocations.map(provider =>
                provider.data
                    .map(({ date, value }) => {
                        return {
                            date: dayjs(date).format(format),
                            provider: provider.source.source,
                            value
                        }
                    })
                    .filter(provider => verifyDateBetweenStandAndEndDays(provider.date))
            )
        )

        const totalAllocations = allAllocations.reduce((total, allocation) => total + allocation.value, 0)

        const totalAllocationsKibana = isNil(kibanaData) ? 0 : kibanaData.all.reduce((total, allocation) => total + allocation.value, 0)

        const allocationByDate = allAllocations.reduce((dates, { date, value }) => {
            dates[date] = (dates[date] || 0) + value
            return dates
        }, {})

        const allocationByProvider = allAllocations.reduce((providers, { provider, value }) => {
            providers[provider] = (providers[provider] || 0) + value
            return providers
        }, {})

        // Group provider with less than 2% of allocation
        const allocationBySignificantProvider = Object.entries(allocationByProvider)
            .filter(([_provider, value]) => value > 0)
            .reduce((providers, [provider, value]) => {
                const percentage = (value * 100) / totalAllocations
                if (percentage < 2) provider = 'Other'
                providers[provider] = (providers[provider] || 0) + value
                return providers
            }, {})

        const kibanaAllocation = isNil(kibanaData)
            ? {}
            : kibanaData.all
                .map(i => [dayjs(i.date).format(format), i.value])
                .filter(([day]) => verifyDateBetweenStandAndEndDays(day))
                .reduce((obj, [date, value]) => {
                    obj[date] = value
                    return obj
                }, {})
        return {
            timeUnit,
            totalAllocations,
            allocationByDate,
            allocationByProvider,
            allocationBySignificantProvider,
            kibanaAllocation,
            totalAllocationsKibana
        }
    }
    return {
        timeUnit,
        totalAllocations: 0,
        allocationByDate: 0,
        allocationByProvider: 0,
        allocationBySignificantProvider: 0,
        kibanaAllocation: 0,
        totalAllocationsKibana: 0,
    }
}


export const mapTaxonomyBreakdown = (isKey, count, data) => {
    const taxonomyBreakdown = []
    for (const key in data) {
        const taxo = data[key]
        if (!isEmpty(taxo)) taxonomyBreakdown.push({ ...taxo, key })
    }

    const sortedClusters = orderBy(taxonomyBreakdown, [isKey, 'key'], ['desc', 'asc'])
    const decimal = isKey === 'affinity' ? 2 : 1
    const dataMapped = take(sortedClusters, count).map((item) => ({ name: item.key, value: nativeRound(+item[isKey], decimal) }))
    return dataMapped
}

export const nativeRound = (num, decimalPlaces = 2) => {
    var p = Math.pow(10, decimalPlaces)
    return Math.round(num * p) / p
}


export const mapCollectOffline = (data) => {
    const { message, total, finished, failed} = data
    const collect = {}
    if(message) {
        return( {
            message,
            success: 0,
            failed: 0,
            total: {
                total: 0
            }
        })
    }
    let totalCount = 0
    if (total && total.failed && total.failed > 0) {
        totalCount += total.failed
        collect.failed = sortedByDate(failed)
    }

    if (total && total.finished  && total.finished > 0) {
        totalCount += total.finished
        collect.success = sortedByDate(finished)
    }
    return {
        ...collect,
        total: {
            success: total.finished || 0,
            failed: total.failed || 0,
            total: totalCount,
            successPercent: totalPercent(total.finished || 0, totalCount),
            failedPercent: totalPercent(total.failed || 0, totalCount),
        }
    }
}


export const mapCollectOnline = (array, isLessThan3Days) => {
    const data = {
        data: array.data.ids[0],
        hours: isLessThan3Days
    }
    return data
}

const totalPercent = (value, total) => {
    const pow = Math.pow(10, 1)
    const round = Math.round(value * pow) / pow
    const percent = (round / total) * 100
    return percent >= 100 || percent <= 0 ? percent : percent.toFixed(1)
}

const sortedByDate = (data) => {
    return Object.keys(data).sort().reduce((r, k) => (r[k] = data[k], r), {})
}



